/**
 * Haptique centralisée — point d'appel UNIQUE pour `navigator.vibrate`.
 *
 * Avant ce module, le seul retour haptique du cockpit était un
 * `navigator.vibrate(10)` codé en dur dans `useLongPress`. Pour étendre la
 * « sensation de l'action » (envoi d'un message, paiement reçu, bascule de
 * sous-vue) sans disperser des magic numbers, on centralise des MOTIFS nommés.
 *
 * Dégradation (le même contrat que les animations CSS) :
 *   - `navigator.vibrate` est ABSENT sur iOS Safari et sur desktop → l'appel
 *     est un no-op silencieux (optional chaining). Le retour VISUEL (scale au
 *     tap, swoosh d'envoi) porte le feedback là où l'haptique n'existe pas.
 *   - On respecte `prefers-reduced-motion: reduce` : un utilisateur qui
 *     demande « réduire les animations » au niveau OS ne veut pas non plus
 *     être vibré à chaque geste. On coupe alors l'haptique (passer
 *     `{ force: true }` pour outrepasser, ex. accusé d'erreur critique).
 *
 * Motifs (ms) volontairement COURTS : un buzz long est désagréable et coûte
 * de la batterie. La plupart sont un simple tick < 12ms.
 */
export type HapticPattern =
  | "tap" // micro-confirmation d'une action légère (envoi d'un message)
  | "select" // bascule discrète (changement d'onglet / de sous-vue)
  | "press" // appui long déclenché (= valeur historique de useLongPress)
  | "success" // évènement positif notable (paiement reçu) — court motif
  | "warning"
  | "error";

const PATTERNS: Record<HapticPattern, number | number[]> = {
  tap: 6,
  select: 8,
  press: 10,
  success: [12, 40, 18],
  warning: [10, 30, 10],
  error: [22, 60, 22],
};

/** True si l'utilisateur a activé « réduire les animations » au niveau OS. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Déclenche un retour haptique. No-op silencieux si l'API est absente
 * (iOS/desktop) ou si l'OS demande de réduire les animations (`force` pour
 * outrepasser).
 */
export function haptic(
  pattern: HapticPattern = "tap",
  opts?: { force?: boolean },
): void {
  if (!opts?.force && prefersReducedMotion()) return;
  navigator.vibrate?.(PATTERNS[pattern]);
}
